/**
 * T0–T5 modal priority framework — the product-facing interruption policy.
 *
 * Tier governs *eligibility* (may this surface interrupt, when, and how often).
 * `ModalPriority` (src/types/modal-queue.types.ts) stays *ordering* (which
 * eligible surface shows first).
 *
 * This module is deliberately ModalId-agnostic and React-free: it maps a
 * surface's tier + the current user/app context to an allow/deny decision. The
 * gate (`evaluateTierGate`) enforces the withholding rules we can drive off real
 * signals today: R1 (clinical lock), R2 (enterprise), and R3 new-user grace.
 *
 * R4 (dismiss memory) is NOT executed here — it's owned by the always-on
 * `modalClosedRules` engine (`src/lib/modalClosedRules.ts`), which already backs
 * the queue's seen/closed filter and carries the real dismiss data (`ModalView`:
 * `consecutive_dismiss_count`, `last_seen_at`, `hide_forever`).
 * `TIER_POLICY[tier].dismissMemory` stays as the declarative per-tier R4 spec,
 * consumed by the `tierClosedRule` bridge (a follow-up — see
 * `modalTiers.handover.md`), not by this gate.
 *
 * Rules and inputs with no FE/DB signal yet — T3 session grace, the returning-user
 * half of grace, per-tier frequency caps (R5) beyond the queue's page-load
 * "one T4/T5 journey" mutex, and format fitness (F8) — are deliberately left
 * out; the plan for each lives in `modalTiers.handover.md`.
 * Product policy transcribed from `.ai-context/modal-priority-stack.md`.
 */

export enum Tier {
  /** T0 — product functionality that *is* the task. Always shows. */
  ProductFunctionality = 0,
  /** T1 — safety & system. Must show, non-dismissible. */
  SafetySystem = 1,
  /** T2 — legal & compliance. Must be acknowledged. Pre-session gate. */
  LegalCompliance = 2,
  /** T3 — revenue & conversion. Receptive moments only, never interrupt. */
  RevenueConversion = 3,
  /** T4 — onboarding & education. Helpful nudge; one per session. */
  OnboardingEducation = 4,
  /** T5 — product & marketing. Heidi's needs; only when settled. */
  ProductMarketing = 5,
}

/** T4/T5: the tiers R5's page-load mutex covers. */
export const isEducationTier = (tier: Tier | undefined): boolean =>
  tier === Tier.OnboardingEducation || tier === Tier.ProductMarketing;

/** R3 — grace: hold a tier back for brand-new users (T5: first 7 days). */
export type GraceRule = { unit: 'days'; count: number };

/**
 * R4 — dismissal-memory spec. `null` on must-show tiers. NOT executed by
 * `evaluateTierGate`; consumed by the `tierClosedRule` bridge into
 * `modalClosedRules` (follow-up — see `modalTiers.handover.md`).
 */
export type DismissMemory = {
  cooldownDays: number;
  /** Suppress permanently once dismissed this many times (T5 = 2). */
  permanentAfterDismissals?: number;
};

export interface TierPolicy {
  /** R1 — may fire during an active recording / clinical session. */
  mayInterruptActiveSession: boolean;
  /** R2 — suppressed entirely for managed / enterprise users. */
  suppressedForEnterprise: boolean;
  /** R3 — new-user grace window; `null` = no grace. */
  grace: GraceRule | null;
  /** R4 — dismissal-memory spec, enforced via `modalClosedRules` (not this gate); `null` = no memory. */
  dismissMemory: DismissMemory | null;
  /** Default dismissibility (ESC / backdrop). */
  defaultDismissible: boolean;
}

/**
 * The tier table, transcribed from `.ai-context/modal-priority-stack.md`. Single
 * source of truth for interruption policy — read by the queue gate, the
 * `tierClosedRule` bridge (R4, follow-up), and docs.
 *
 * Two values were judgment calls where the doc didn't state a cell (flagged for
 * review): T0 `defaultDismissible`, and T2 `mayInterruptActiveSession` (with the
 * promote-to-T1 note).
 *
 * Per-tier frequency caps, T3's first-3-sessions grace, and format fitness are
 * absent by design — they have no reliable FE signal yet. See
 * `modalTiers.handover.md`.
 */
export const TIER_POLICY: Record<Tier, TierPolicy> = {
  [Tier.ProductFunctionality]: {
    mayInterruptActiveSession: true,
    suppressedForEnterprise: false,
    grace: null,
    dismissMemory: null,
    defaultDismissible: true,
  },
  [Tier.SafetySystem]: {
    mayInterruptActiveSession: true,
    suppressedForEnterprise: false,
    grace: null,
    dismissMemory: null,
    defaultDismissible: false, // non-dismissible: user can't safely continue without it
  },
  [Tier.LegalCompliance]: {
    // Pre-session gate. A consent requirement that arises mid-session must be
    // promoted to T1 (or handled outside the queue) — see the T2 timing note.
    mayInterruptActiveSession: false,
    suppressedForEnterprise: false,
    grace: null,
    dismissMemory: null,
    defaultDismissible: false, // must acknowledge before proceeding
  },
  [Tier.RevenueConversion]: {
    mayInterruptActiveSession: false,
    suppressedForEnterprise: true,
    grace: null, // "first 3 sessions" grace pending a session counter — see handover
    dismissMemory: null,
    defaultDismissible: true,
  },
  [Tier.OnboardingEducation]: {
    mayInterruptActiveSession: false,
    suppressedForEnterprise: true,
    grace: null,
    dismissMemory: { cooldownDays: 7 }, // R4 spec — realized via modalClosedRules
    defaultDismissible: true,
  },
  [Tier.ProductMarketing]: {
    mayInterruptActiveSession: false,
    suppressedForEnterprise: true,
    grace: { unit: 'days', count: 7 }, // new-user only; returning-user half pending a recency signal
    dismissMemory: { cooldownDays: 7, permanentAfterDismissals: 2 }, // R4 spec — realized via modalClosedRules
    defaultDismissible: true,
  },
};

/** Why a surface was withheld — drives the debug panel and analytics. */
export type TierGateReason =
  | 'recording-active' // R1
  | 'enterprise-suppressed' // R2
  | 'in-grace-period'; // R3

export type TierGateResult =
  | { allowed: true }
  | { allowed: false; reason: TierGateReason };

/** Everything the gate needs to decide, resolved by the caller (queue atom). */
export interface TierGateContext {
  /** R1 — a recording/clinical session timer is running. */
  isRecordingActive: boolean;
  /** R2 — managed / enterprise user. */
  isEnterprise: boolean;
  /** R3 — account age in days, for the day-based grace (T5 = first 7 days). */
  accountAgeDays: number;
}

const denyDisplay = (reason: TierGateReason): TierGateResult => ({
  allowed: false,
  reason,
});

/**
 * Evaluate a single surface against its tier's policy (withholding Rules R1–R3).
 * Pure.
 *
 * R4 (dismiss memory) is not evaluated here — it's owned by `modalClosedRules`
 * (see the module doc-comment). The cross-tier "one registered T4/T5 journey
 * per page load" mutex (R5) and the queue+`ModalId` requirement (R6) span multiple surfaces, so
 * they are enforced at the call site (the queue atom), not here.
 */
export const evaluateTierGate = (tier: Tier, ctx: TierGateContext): TierGateResult => {
  const policy = TIER_POLICY[tier];

  // R1 — clinical moment lock.
  if (ctx.isRecordingActive && !policy.mayInterruptActiveSession) {
    return denyDisplay('recording-active');
  }

  // R2 — enterprise lock.
  if (ctx.isEnterprise && policy.suppressedForEnterprise) {
    return denyDisplay('enterprise-suppressed');
  }

  // R3 — new-user grace period.
  if (policy.grace && ctx.accountAgeDays < policy.grace.count) {
    return denyDisplay('in-grace-period');
  }

  return { allowed: true };
};

/**
 * Live signals the resolver maps into a {@link TierGateContext}. Sourced from
 * `appStateForModalsAtom` by the queue at eval time; kept as a plain structural
 * input so this module stays React-free and unit-testable.
 */
export interface TierGateSignals {
  /** R1 — clinical recording active (incl. paused / Chronicle). `appState.isClinicalRecordingActive`. */
  isClinicalRecordingActive: boolean;
  /** R2 — managed / enterprise account. `appState.isUserManaged`. */
  isUserManaged: boolean;
  /** R3 — account ≥ 7 days old. `appState.isUserAtLeastAWeekOld`. */
  isUserAtLeastAWeekOld: boolean;
}

export const resolveTierGateContext = (signals: TierGateSignals): TierGateContext => ({
  isRecordingActive: signals.isClinicalRecordingActive, // R1
  isEnterprise: signals.isUserManaged, // R2
  // R3 (T5 day-grace): the only day-grace in the table is T5 = 7 days, so the
  // 7-day signal is all we need — 7 clears it, 0 is inside it.
  accountAgeDays: signals.isUserAtLeastAWeekOld ? 7 : 0,
});
